import React, { useEffect, useRef, useState } from "react";
import { FaCheck, FaTimes } from "react-icons/fa";
import { Tabs } from "rsuite";
import { toast } from "react-toastify";
import "./Requests.css";
import ConfirmActionModal from "../AllComponents/Inventory/ConfirmActionModal";
import PaginationComponent from "../AllComponents/Shared/PaginationComponent";
import {
  getPendingRequests,
  getAllUsersByStatus,
  searchUsersByStatus,
  approveRequest,
  rejectRequest,
  rejectApprovedUser,
} from "../../api/admin/inventory";

const Requests = () => {
  const [search, setSearch] = useState("");
  const [requests, setRequests] = useState([]); // Initialize with empty array
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const limitOptions = [10, 20, 30, 50, 100];
  const [loading, setLoading] = useState(true); // Start with loading true
  const [error, setError] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { type: "approve" | "reject", request: {...} }
  const [activeTab, setActiveTab] = useState("pending"); // "pending", "approved", "rejected"
  const [statusCounts, setStatusCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasNext: false,
    hasPrev: false,
  });
  const searchTimeoutRef = useRef(null);

  // Filter requests by status
  const getRequestsByStatus = (status) => {
    return requests.filter((req) => (req.status || "pending") === status);
  };

  const getSearchResultsByStatus = (status) => {
    return searchResults.filter((req) => (req.status || "pending") === status);
  };

  // Get filtered list based on active tab
  const getFilteredList = () => {
    const baseList = search.trim() && !loading ? searchResults : requests;
    return baseList.filter((req) => (req.status || "pending") === activeTab);
  };

  const allFiltered = getFilteredList();
  
  // Calculate paginated results (client-side pagination for search, server-side for normal)
  const isSearching = search.trim() && !searchLoading;
  const startIndex = isSearching ? (page - 1) * limit : 0;
  const endIndex = isSearching ? startIndex + limit : allFiltered.length;
  const filtered = isSearching ? allFiltered.slice(startIndex, endIndex) : allFiltered;
  
  // Calculate total items based on whether we're searching or not
  const totalItems = isSearching 
    ? allFiltered.length 
    : (pagination.totalCount || getRequestsByStatus(activeTab).length);

  // Fetch counts for all statuses on initial load
  const fetchAllCounts = async () => {
    try {
      const [pendingRes, approvedRes, rejectedRes] = await Promise.all([
        getAllUsersByStatus({ status: "pending", page: 1, limit: 1 }),
        getAllUsersByStatus({ status: "approved", page: 1, limit: 1 }),
        getAllUsersByStatus({ status: "rejected", page: 1, limit: 1 }),
      ]);

      const pendingData = pendingRes?.data?.data || pendingRes?.data;
      const approvedData = approvedRes?.data?.data || approvedRes?.data;
      const rejectedData = rejectedRes?.data?.data || rejectedRes?.data;

      setStatusCounts({
        pending: pendingData?.pagination?.totalCount || 0,
        approved: approvedData?.pagination?.totalCount || 0,
        rejected: rejectedData?.pagination?.totalCount || 0,
      });
    } catch (err) {
      console.error("Failed to fetch counts", err);
      // Don't show error toast for counts, just log it
    }
  };

  // Reusable function to fetch users by status with pagination
  const fetchRequests = async (status = activeTab, pageNum = page) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getAllUsersByStatus({
        status: status,
        page: pageNum,
        limit: limit,
      });
      
      // API response structure: { data: { users: [], pagination: {}, status: "approved" } }
      const responseData = data?.data || data;
      
      // Extract users array
      const users = responseData?.users || [];
      
      // Map users with status from response
      const usersWithStatus = users.map(user => ({
        ...user,
        status: responseData?.status || status,
      }));
      
      // Update pagination from API
      if (responseData?.pagination) {
        setPagination({
          currentPage: responseData.pagination.currentPage || pageNum,
          totalPages: responseData.pagination.totalPages || 1,
          totalCount: responseData.pagination.totalCount || 0,
          limit: responseData.pagination.limit || limit,
          hasNext: responseData.pagination.hasNext || false,
          hasPrev: responseData.pagination.hasPrev || false,
        });
        
        // Update status count for current tab
        setStatusCounts(prev => ({
          ...prev,
          [status]: responseData.pagination.totalCount || 0,
        }));
      }
      
      setRequests(usersWithStatus);
      setError(null);
    } catch (err) {
      console.error("Failed to load requests", err);
      setError("Failed to load requests. Please try again.");
      setRequests([]);
      toast.error("Failed to load requests. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch counts for all tabs on mount
  useEffect(() => {
    fetchAllCounts();
  }, []);

  // Fetch requests on mount and when tab or page changes
  useEffect(() => {
    fetchRequests(activeTab, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, limit]);

  // Reset to first page when search term or active tab changes
  useEffect(() => {
    setPage(1);
  }, [search, activeTab]);

  // Debounced search effect for requests
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const query = search.trim().toLowerCase();

    if (!query) {
      setSearchResults([]);
      setSearchLoading(false);
      // Reset pagination when search is cleared - will be updated by fetchRequests
      return;
    }

    setSearchLoading(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        // Call search API with query and current tab status
        const { data } = await searchUsersByStatus({
          search: query,
          status: activeTab, // Search within the active tab's status
          page: 1,
          limit: 1000, // Get all results for search
        });
        console.log(data);
        
        // API response structure: { data: { approved: { users: [], count: }, pending: { users: [], count: }, rejected: { users: [], count: } } }
        const responseData = data?.data || data;
        
        // Extract users from the status category that matches activeTab
        let users = [];
        if (activeTab === "approved") {
          users = responseData?.status==="approved"?responseData?.users:[];
        } else if (activeTab === "pending") {
          users = responseData?.status==="pending"?responseData?.users:[];
        } else if (activeTab === "rejected") {
          users = responseData?.status==="rejected"?responseData?.users:[];
        }
        console.log("users",users);
        
        
        // Map users with status
        const resultsWithStatus = users.map(user => ({
          ...user,
          status: activeTab,
        }));
        
        setSearchResults(resultsWithStatus);
        
        // Update pagination state for search results
        if (responseData?.pagination) {
          setPagination({
            currentPage: 1, // Reset to first page for search
            totalPages: Math.ceil((responseData.pagination.totalCount || resultsWithStatus.length) / limit),
            totalCount: responseData.pagination.totalCount || resultsWithStatus.length,
            limit: limit,
            hasNext: (responseData.pagination.totalCount || resultsWithStatus.length) > limit,
            hasPrev: false,
          });
        } else {
          // Fallback pagination calculation
          const totalCount = resultsWithStatus.length;
          setPagination({
            currentPage: 1,
            totalPages: Math.ceil(totalCount / limit),
            totalCount: totalCount,
            limit: limit,
            hasNext: totalCount > limit,
            hasPrev: false,
          });
        }
      } catch (err) {
        console.error("Failed to search requests, using client-side search", err);
        // Fallback to client-side search within current tab
        const filtered = requests.filter(
          (req) =>
            ((req.status || "pending") === activeTab) &&
            (
              (req.name || req.fullName || "").toLowerCase().includes(query) ||
              (req.email || "").toLowerCase().includes(query) ||
              (req.phone || req.contact || "").includes(query)
            )
        );
        setSearchResults(filtered);
      } finally {
        setSearchLoading(false);
      }
    }, 800);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search, activeTab]);

  // Reset to first page when page size changes
  useEffect(() => {
    setPage(1);
  }, [limit]);

  // Update pagination state when page changes during search
  useEffect(() => {
    if (search.trim() && searchResults.length > 0) {
      const totalCount = searchResults.filter((req) => (req.status || "pending") === activeTab).length;
      const totalPages = Math.ceil(totalCount / limit);
      setPagination(prev => ({
        ...prev,
        currentPage: page,
        totalPages: totalPages,
        totalCount: totalCount,
        hasNext: page < totalPages,
        hasPrev: page > 1,
      }));
    }
  }, [page, search, searchResults, activeTab, limit]);

  

  const handleApproveClick = (req) => {
    setConfirmAction({ type: "approve", request: req });
  };

  const handleRejectClick = (req) => {
    setConfirmAction({ type: "reject", request: req });
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    
    const { type, request } = confirmAction;
    const id = request._id || request.id;
    
    setProcessingId(id);
    try {
      // Call the appropriate API based on action type and current tab
      if (type === "approve") {
        await approveRequest(id);
      } else {
        // Use different endpoint for rejecting approved users vs pending users
        if (activeTab === "approved") {
          await rejectApprovedUser(id);
        } else {
          await rejectRequest(id);
        }
      }
      
      // Refetch requests after successful action
      await fetchRequests(activeTab, page);
      
      // Refetch all counts to update tab badges
      await fetchAllCounts();
      
      // Clear search results to force refresh
      setSearchResults([]);
      
      // Show success toast
      try {
        toast.success(
          type === "approve"
            ? "User approved successfully!"
            : "User rejected successfully!"
        );
      } catch (toastErr) {
        console.log(
          "Toast notification:",
          type === "approve"
            ? "User approved successfully!"
            : "User rejected successfully!"
        );
      }
      
      setConfirmAction(null);
    } catch (err) {
      console.error(`Failed to ${type} request`, err);
      const errorMessage = err.response?.data?.message || err.message || `Failed to ${type} request. Please try again.`;
      try {
        toast.error(errorMessage);
      } catch (toastErr) {
        console.error(errorMessage);
      }
    } finally {
      setProcessingId(null);
    }
  };

  const handleCancelAction = () => {
    if (processingId) return; // Prevent closing while processing
    setConfirmAction(null);
  };

  return (
    <div className="req-surface">
      <div className="req-shell">
        <header className="req-hero">
          <div className="req-hero-copy">
            <p className="req-chip">User requests</p>
            <h1>
              Registration management
              <span> at your fingertips.</span>
            </h1>
          </div>
        </header>

        <section className="req-tabs-section">
          <Tabs
            activeKey={activeTab}
            onSelect={(key) => {
              setActiveTab(key);
              setPage(1);
              setSearch("");
              setSearchResults([]);
            }}
            appearance="subtle"
            style={{ marginBottom: "20px", "--rs-tabs-active-color": "#198754" }}
          >
            <Tabs.Tab eventKey="pending" title={`Pending (${statusCounts.pending})`} />
            <Tabs.Tab eventKey="approved" title={`Approved (${statusCounts.approved})`} />
            <Tabs.Tab eventKey="rejected" title={`Rejected (${statusCounts.rejected})`} />
          </Tabs>
        </section>

        <section className="req-search-block">
          <div className="req-search-field">
            <svg
              className="req-search-icon"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
              />
            </svg>
            <input
              type="text"
              placeholder="Search by name, email, or phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <span className="req-count">
            {search
              ? `Showing ${filtered.length} of ${totalItems} ${activeTab} requests`
              : `All ${activeTab} requests (${totalItems})`}
          </span>
        </section>

        <section className="req-table-wrapper">
          <table className="req-table">
            <thead>
              <tr>
                <th>Full Name</th>
                <th>Phone</th>
                <th>Email</th>
                <th>User Type</th>
                <th className="req-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading || searchLoading ? (
                Array.from({ length: limit }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="req-skeleton-row">
                    <td>
                      <div className="req-skeleton-cell"></div>
                    </td>
                    <td>
                      <div className="req-skeleton-cell"></div>
                    </td>
                    <td>
                      <div className="req-skeleton-cell"></div>
                    </td>
                    <td>
                      <div className="req-skeleton-cell"></div>
                    </td>
                    <td className="req-actions">
                      <div className="req-skeleton-actions">
                        <div className="req-skeleton-button"></div>
                        <div className="req-skeleton-button"></div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : error ? (
                <tr>
                  <td colSpan={5} className="req-empty">
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" }}>
                      <span style={{ fontSize: "2rem" }}>⚠️</span>
                      <div>
                        <strong>Failed to load requests</strong>
                        <p style={{ margin: "8px 0 0", color: "#6b7280", fontSize: "0.9rem" }}>
                          {error}
                        </p>
                      </div>
                      <button
                        onClick={fetchRequests}
                        style={{
                          padding: "8px 16px",
                          borderRadius: "8px",
                          border: "1px solid #198754",
                          background: "#198754",
                          color: "white",
                          cursor: "pointer",
                          fontSize: "0.9rem",
                          fontWeight: 500,
                        }}
                      >
                        Retry
                      </button>
                    </div>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} className="req-empty">
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "2rem" }}>
                        {activeTab === "pending" ? "📋" : activeTab === "approved" ? "✅" : "❌"}
                      </span>
                      <div>
                        <strong>
                          {search
                            ? "No requests match your search"
                            : activeTab === "pending"
                            ? "No pending requests"
                            : activeTab === "approved"
                            ? "No approved requests"
                            : "No rejected requests"}
                        </strong>
                        {!search && (
                          <p style={{ margin: "8px 0 0", color: "#6b7280", fontSize: "0.9rem" }}>
                            {activeTab === "pending"
                              ? "All registration requests have been processed."
                              : activeTab === "approved"
                              ? "No users have been approved yet."
                              : "No users have been rejected yet."}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((req) => {
                  
                  const reqId = req._id || req.id;
                  const isProcessing = processingId === reqId;
                  const status = req.status || "pending";
                  return (
                    <tr key={reqId}>
                      <td>{req.name || req.fullName || "N/A"}</td>
                      <td>{req.phone || req.contact || "N/A"}</td>
                      <td>{req.email || "N/A"}</td>
                      <td>
                        <span
                          style={{
                            padding: "4px 12px",
                            borderRadius: "12px",
                            fontSize: "0.85rem",
                            fontWeight: 500,
                            backgroundColor: "rgba(25, 135, 84, 0.15)",
                            color: "#198754",
                            textTransform: "capitalize",
                          }}
                        >
                          {req.userType || "N/A"}
                        </span>
                      </td>
                      <td className="req-actions">
                        {status === "pending" ? (
                          <>
                            <button
                              type="button"
                              className="req-btn req-btn-approve"
                              onClick={() => handleApproveClick(req)}
                              disabled={isProcessing}
                              aria-label="Approve request"
                              title="Approve"
                            >
                              <FaCheck />
                            </button>
                            <button
                              type="button"
                              className="req-btn req-btn-reject"
                              onClick={() => handleRejectClick(req)}
                              disabled={isProcessing}
                              aria-label="Reject request"
                              title="Reject"
                            >
                              <FaTimes />
                            </button>
                          </>
                        ) : status === "approved" ? (
                          <>
                            <span
                              style={{
                                padding: "4px 12px",
                                borderRadius: "12px",
                                fontSize: "0.85rem",
                                fontWeight: 500,
                                backgroundColor: "rgba(25, 135, 84, 0.15)",
                                color: "#198754",
                                marginRight: "8px",
                              }}
                            >
                              Approved
                            </span>
                            <button
                              type="button"
                              className="req-btn req-btn-reject"
                              onClick={() => handleRejectClick(req)}
                              disabled={isProcessing}
                              aria-label="Reject user"
                              title="Reject this approved user"
                            >
                              <FaTimes />
                            </button>
                          </>
                        ) : (
                          <>
                            <span
                              style={{
                                padding: "4px 12px",
                                borderRadius: "12px",
                                fontSize: "0.85rem",
                                fontWeight: 500,
                                backgroundColor: "rgba(220, 38, 38, 0.15)",
                                color: "#dc2626",
                                marginRight: "8px",
                              }}
                            >
                              Rejected
                            </span>
                            <button
                              type="button"
                              className="req-btn req-btn-approve"
                              onClick={() => handleApproveClick(req)}
                              disabled={isProcessing}
                              aria-label="Approve user"
                              title="Approve this rejected user"
                            >
                              <FaCheck />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </section>

        {!loading && !error && !searchLoading && totalItems > limit && (
          <PaginationComponent
            total={totalItems}
            limit={limit}
            limitOptions={limitOptions}
            activePage={page}
            onChangePage={(newPage) => {
              setPage(newPage);
              // Only fetch from server if not searching (client-side pagination for search)
              if (!search.trim()) {
                fetchRequests(activeTab, newPage);
              }
            }}
            onChangeLimit={(nextLimit) => {
              setLimit(nextLimit);
              setPage(1);
              // Recalculate pagination for search results
              if (search.trim() && searchResults.length > 0) {
                const totalCount = searchResults.filter((req) => (req.status || "pending") === activeTab).length;
                setPagination(prev => ({
                  ...prev,
                  totalPages: Math.ceil(totalCount / nextLimit),
                  limit: nextLimit,
                  hasNext: totalCount > nextLimit,
                }));
              }
            }}
            className="req-pagination"
          />
        )}

        <ConfirmActionModal
          isOpen={!!confirmAction}
          actionType={confirmAction?.type}
          title={
            confirmAction?.type === "approve"
              ? activeTab === "rejected"
                ? "Approve User"
                : "Approve Registration Request"
              : activeTab === "approved"
              ? "Reject User"
              : "Reject Registration Request"
          }
          message={
            confirmAction?.request
              ? confirmAction.type === "approve"
                ? activeTab === "rejected"
                  ? `Are you sure you want to approve "${confirmAction.request.name || confirmAction.request.fullName || "this user"}"? They will be moved to approved users and will be able to login.`
                  : `Are you sure you want to approve the registration request from "${confirmAction.request.name || confirmAction.request.fullName || "this user"}"? They will be able to login after approval.`
                : activeTab === "approved"
                ? `Are you sure you want to reject "${confirmAction.request.name || confirmAction.request.fullName || "this user"}"? They will be moved to rejected users and will not be able to login.`
                : `Are you sure you want to reject the registration request from "${confirmAction.request.name || confirmAction.request.fullName || "this user"}"? This action cannot be undone.`
              : ""
          }
          onCancel={handleCancelAction}
          onConfirm={handleConfirmAction}
          confirming={
            processingId === (confirmAction?.request?._id || confirmAction?.request?.id)
          }
        />
      </div>
    </div>
  );
};

export default Requests;

